"use client"

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { 
  ChevronDown, 
  Shield, 
  Server, 
  PenTool, 
  Cpu, 
  Sun,
  Menu,
  Zap,
  X,
  Wrench
} from 'lucide-react'

// Add this type for our industries
type Industry = {
  name: string
  icon: React.ElementType
  href: string
}

// Create an industries array
const industries: Industry[] = [
  { name: "Cyber Security", icon: Shield, href: "/ai-in-cybersecurity-sales" },
  { name: "MSPs", icon: Server, href: "/ai-in-cybersecurity-sales" },
  { name: "Marketing Agencies", icon: PenTool, href: "/ai-for-marketing-agencies" },
  { name: "Tech Companies", icon: Cpu, href: "/ai-in-tech-sales" },
  { name: "Solar & Renewables", icon: Sun, href: "/ai-for-solar-sales" },
  { name: "SAAS", icon: Zap, href: "/ai-in-saas" },
  { name: "Local Contractors", icon: Wrench, href: "/ai-call-management-for-local-contractors" },
]

export default function Header() {
  // Move state initialization to useEffect to avoid hydration mismatch
  const [mounted, setMounted] = useState(false)
  const [isIndustriesOpen, setIsIndustriesOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Only render interactive elements after initial mount
  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsIndustriesOpen(false)
      }
    }

    if (mounted) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [mounted])

  return (
    <header className="px-4 lg:px-6 h-16 flex items-center border-b bg-white/80 backdrop-blur-sm dark:bg-gray-800/80 fixed w-full z-50">
      <Link className="flex items-center justify-center" href="/">
        <Image src="/summit-growth-logo.png" alt="Summit Growth Logo" width={160} height={40} />
      </Link>
      
      {/* Desktop Navigation - Only show after mounted */}
      {mounted && (
        <>
          <nav className="hidden md:flex ml-auto items-center gap-4 sm:gap-6">
            {/* Industries Dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsIndustriesOpen(!isIndustriesOpen)}
                className="flex items-center gap-1 text-sm font-medium hover:text-blue-600 dark:hover:text-blue-400"
              >
                Industries
                <ChevronDown className="h-4 w-4" />
              </button>

              {/* Modified Mega Menu */}
              {isIndustriesOpen && (
                <div className="absolute top-full left-0 mt-2 w-[280px] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-4">
                  <div className="px-4">
                    {industries.map((industry) => (
                      <Link
                        key={industry.name}
                        href={industry.href}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <industry.icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-gray-900 dark:text-white">{industry.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link className="text-sm font-medium hover:text-blue-600 dark:hover:text-blue-400" href="/services">
              Services
            </Link>

            <Link className="text-sm font-medium hover:text-blue-600 dark:hover:text-blue-400" href="/blog">
              Blog
            </Link>

            <Link className="text-sm font-medium hover:text-blue-600 dark:hover:text-blue-400" href="/book-a-demo">
              Book A Call
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="ml-auto md:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-600 dark:text-gray-300" />
            ) : (
              <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
            )}
          </button>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="absolute top-16 left-0 right-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 md:hidden">
              <div className="px-4 py-6 space-y-4">
                {/* Mobile Industries Section */}
                <div className="space-y-4">
                  <div className="font-medium text-gray-900 dark:text-white">Industries</div>
                  <div className="pl-4 space-y-3">
                    {industries.map((industry) => (
                      <Link
                        key={industry.name}
                        href={industry.href}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <industry.icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{industry.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Mobile Navigation Links */}
                <div className="space-y-3">
                  <Link
                    href="/services"
                    className="block p-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Services
                  </Link>
                  <Link
                    href="/blog"
                    className="block p-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Blog
                  </Link>
                  <Link
                    href="/solutions"
                    className="block p-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Solutions
                  </Link>
                  <Link
                    href="/about"
                    className="block p-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    About
                  </Link>
                  <Link
                    href="/contact"
                    className="block p-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </header>
  )
}